import React, { useEffect, useRef, useState } from 'react';
import { hasMergeIntent } from '@ocnexus/contracts';
import { api, authHeaders } from '../../api';

export type ProjectSyncTask = {
  id: string;
  priority: 'P0' | 'P1' | 'P2' | 'P3';
  status: string;
  suppression_reason?: string | null;
  title: string;
  category?: string;
  phase_id?: string | null;
  description?: string;
  file_path?: string;
  /** Set once the fix has opened its pull request on the repo. */
  remediation_pr_number?: number | null;
  last_event_message?: string | null;
  /** Null means the scoped queue read confirmed no active request; absent is unknown. */
  fix_queue?: { state: 'queued' | 'running'; reason: string | null } | null;
};

type SyncStatus = { last_completed_at?: string | null; last_run?: { status?: string } | null };

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';

/** Draft text only; the parent owns sending so this control can never start work. */
export function composeFixSelectedMessage(tasks: ProjectSyncTask[], selectedIds: Set<string>, guardian: boolean): string {
  const selected = tasks.filter((task) => selectedIds.has(task.id));
  const items = selected.map((task) => `- [${task.priority}] ${task.title}${task.file_path ? ` (${task.file_path})` : ''}`);
  if (guardian) items.push('- Turn on Guardian to watch for regressions on every push');
  return `Please fix the following items:\n${items.join('\n')}`;
}

/**
 * What a typed fix request means in task ids, or null when the text names no
 * set (the checked boxes then stand). 'fix all the issues' must mean every open
 * issue, not only the pre-checked critical + high ones (canary 2026-10-03: 32
 * open, 17 pre-checked, 'fix all the issues' ran 17).
 */
export function idsForTypedFixIntent(text: string, tasks: ProjectSyncTask[]): string[] | null {
  const t = text.trim().toLowerCase();
  // A drafted checklist request (or anything long) carries its own explicit list.
  if (!t || t.length > 160 || t.startsWith('please fix the following items')) return null;
  if (!/\b(fix|resolve|repair|address|patch)\b/.test(t)) return null;
  // 'fix the checked ones' / 'fix these' defers to the checkboxes.
  if (/\b(checked|selected|ticked|these|those|them)\b/.test(t) && !/\b(all|every|everything)\b/.test(t)) return null;
  // Only a whole-message command can name a bulk set. Finding prose (including
  // an edited generated draft with [HIGH]) never becomes a priority command.
  if (/^(?:(?:please|can you|could you)\s+)?(?:fix|resolve|repair|address|patch)\s+them\s+all[.!]?$/i.test(t)) return tasks.map((task) => task.id);
  if (!/^(?:(?:please|can you|could you)\s+)?(?:fix|resolve|repair|address|patch)\s+(?:(?:all|every|everything)(?:\s+(?:of\s+)?(?:the\s+)?(?:issues?|findings?|items?|ones|them))?|(?:the\s+)?(?:p0|p1|criticals?|high)(?:\s+(?:and|\+)\s+(?:p0|p1|criticals?|high))?(?:\s+(?:priority\s+)?(?:issues?|findings?|items?|ones))?(?:\s+only)?)[.!]?$/i.test(t)) return null;
  const wantsP0 = /\b(p0|criticals?)\b/.test(t);
  const wantsP1 = /\b(p1|high)\b/.test(t);
  if (wantsP0 || wantsP1) {
    return tasks.filter((task) => (wantsP0 && task.priority === 'P0') || (wantsP1 && task.priority === 'P1')).map((task) => task.id);
  }
  if (/\b(all|every|everything)\b/.test(t)) return tasks.map((task) => task.id);
  return null;
}

/**
 * Does this typed message ask for fixes? Only then may the ticked issues ride
 * along and launch. Canary 2026-10-03: 'merge the open PR' was sent with 14
 * issues pre-ticked and started 14 fixes nobody asked for.
 */
export function hasFixIntent(text: string): boolean {
  return /\b(fix|fixes|resolve|repair|address|patch)\b/i.test(text);
}

/** Checked defaults are only a request when the subscriber names that set.
 * A specific finding draft must never inherit unrelated prechecked issues. */
export function usesCheckedFixIntent(text: string): boolean {
  const t = text.trim();
  return /^(?:(?:please|can you|could you)\s+)?(?:fix|resolve|repair|address|patch)\s+(?:the\s+)?(?:checked|selected|ticked|these|those|them)(?:\s+(?:issues?|findings?|items?|ones))?[.!]?$/i.test(t);
}

/** Resolve one submitted message without changing the live checkbox selection. */
export function composeFixSubmission(input: { typed: string; tasks: ProjectSyncTask[]; checkedIds: readonly string[];
  checkedDraft: string | null; allowEmpty: boolean; projectFixMode: boolean; targetIds?: string[] }):
  { message: string; fixTaskIds: string[]; error?: string } {
  const { typed, tasks } = input;
  if ((hasMergeIntent as (value: unknown) => boolean)(typed)) return { message: typed, fixTaskIds: [] };
  let fixTaskIds = [...(input.targetIds ?? input.checkedIds)];
  let selection = input.targetIds ? composeFixSelectedMessage(tasks, new Set(input.targetIds), false) : input.checkedDraft;
  const typedIds = input.targetIds || (selection && typed.includes(selection)) ? null : idsForTypedFixIntent(typed, tasks);
  if (typedIds !== null) {
    fixTaskIds = typedIds;
    selection = typedIds.length ? composeFixSelectedMessage(tasks, new Set(typedIds), false) : null;
  }
  if (input.projectFixMode && typed && hasFixIntent(typed) && !input.targetIds && typedIds === null
    && (!selection || !typed.includes(selection)) && !usesCheckedFixIntent(typed)) return { message: '', fixTaskIds: [],
    error: 'Choose the exact issues in the checklist and press Fix selected, or explicitly ask to fix all issues or a priority such as P1 only. I could not determine which issues this draft requests.' };
  if (input.projectFixMode && hasFixIntent(typed) && fixTaskIds.length === 0 && (typedIds !== null || usesCheckedFixIntent(typed)))
    return { message: '', fixTaskIds: [], error: 'No current issues are selected for that request. Select issues in the checklist and press Fix selected.' };
  if (!input.targetIds && typedIds === null && typed && (!selection || !typed.includes(selection)) && !usesCheckedFixIntent(typed)) {
    selection = null;
    fixTaskIds = [];
  }
  const message = selection && typed && !typed.includes(selection) ? `${typed}\n\n${selection}`
    : typed || (input.allowEmpty ? selection : null) || '';
  return { message, fixTaskIds: hasFixIntent(message) ? fixTaskIds : [] };
}

const PRIORITY_COLOR: Record<ProjectSyncTask['priority'], string> = {
  P0: 'var(--danger, #ef4444)',
  P1: 'var(--warning, #f59e0b)',
  P2: 'var(--text-dim, #9ca3af)',
  P3: 'var(--text-dim, #9ca3af)',
};

function oneLine(text?: string): string {
  return text?.split(/\r?\n/).find(Boolean)?.trim() || 'Review and fix this issue.';
}

/** Every task on the project, whatever its status, for watching fixes in flight. */
export async function loadProjectTasks(projectId: string): Promise<ProjectSyncTask[]> {
  const headers = await authHeaders();
  const response = await fetch(`${API_BASE}/api/sync/tasks/${encodeURIComponent(projectId)}?limit=500`, { headers });
  if (!response.ok) throw new Error('Could not load sync tasks');
  const body = await response.json() as { tasks?: ProjectSyncTask[] };
  return body.tasks ?? [];
}

/** One issue across syncs: each sync re-emits the same finding as a new task row. */
function issueKey(task: ProjectSyncTask): string {
  const t = task as ProjectSyncTask & { file?: string; phase?: string };
  return JSON.stringify([t.category ?? '', t.phase_id ?? t.phase ?? '', (t.title ?? '').trim().toLowerCase(), t.file ?? t.file_path ?? '', t.description ?? '']);
}

/**
 * The issues to offer for fixing, one per issue. Each sync re-emits the same
 * task, so the raw list repeats items 5x (canary 2026-10-03: 'AGENTS.md not
 * found' x5). An issue whose fix is already in flight on any of its copies is
 * left out: offering the sibling copy again would open a duplicate pull request.
 */
export function selectFixableTasks(all: ProjectSyncTask[]): ProjectSyncTask[] {
  // A queued request leaves its task open/failed until a worker fences it.
  const inFlight = new Set(all.filter((task) => task.status === 'in_progress'
    || task.fix_queue?.state === 'queued' || task.fix_queue?.state === 'running').map(issueKey));
  const seen = new Set<string>();
  return all.filter((task) => {
    // A failed fix is still an unfixed issue: keep it listed so it can be retried
    // (it used to vanish from the checklist with no way to try again).
    if (task.status !== 'open' && task.status !== 'failed') return false;
    const key = issueKey(task);
    if (inFlight.has(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

async function loadOpenTasks(projectId: string): Promise<ProjectSyncTask[]> {
  return selectFixableTasks(await loadProjectTasks(projectId));
}

export function ProjectFixChecklistContent({ tasks, selected, guardianAvailable, guardian, onToggleTask, onToggleGuardian, onDraft, lastSyncedAt, onSelectSet, disabled = false }: {
  tasks: ProjectSyncTask[];
  selected: Set<string>;
  guardianAvailable: boolean;
  guardian: boolean;
  onToggleTask: (id: string) => void;
  onToggleGuardian: () => void;
  onDraft: () => void;
  lastSyncedAt?: string | null;
  /** Replace the whole selection (quick actions). */
  onSelectSet?: (ids: Set<string>) => void;
  disabled?: boolean;
}): React.ReactElement {
  const selectedCount = selected.size + (guardian ? 1 : 0);
  const counts = (['P0', 'P1', 'P2', 'P3'] as const)
    .map((p) => [p, tasks.filter((t) => t.priority === p).length] as const)
    .filter(([, n]) => n > 0)
    .map(([p, n]) => `${n} ${p === 'P0' ? 'critical' : p === 'P1' ? 'high' : p === 'P2' ? 'medium' : 'low'}`)
    .join(', ');
  const urgent = tasks.filter((t) => t.priority === 'P0' || t.priority === 'P1').length;
  const when = lastSyncedAt ? ` (${new Date(lastSyncedAt).toLocaleString()})` : '';
  return (
    <section data-testid="post-sync-next-step" aria-label="Fix checklist" style={{ padding: 16, border: '1px solid var(--border, #ddd)', borderRadius: 10, margin: '12px 0' }}>
      {tasks.length > 0 && <p data-testid="post-sync-summary" style={{ margin: '0 0 8px' }}>
        Your last sync{when} found <strong>{tasks.length} open issues</strong>: {counts}.
        {urgent > 0 ? ` Review the ${urgent} critical and high-priority issues, adjust your selection, then press Fix selected.` : ' Pick what you want fixed below.'}
      </p>}
      <h2>Check what you want fixed.</h2>
      {tasks.length > 0 && onSelectSet && (
        <div data-testid="fix-quick-actions" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, margin: '0 0 12px' }}>
          {/* These only choose what is ticked; Fix selected is the one button that starts work. */}
          <span style={{ fontSize: 13, opacity: 0.8 }}>Select:</span>
          {([
            ['All', tasks.map((t) => t.id)],
            ['P0 only', tasks.filter((t) => t.priority === 'P0').map((t) => t.id)],
            ['P1 only', tasks.filter((t) => t.priority === 'P1').map((t) => t.id)],
            ['Clear', [] as string[]],
          ] as Array<[string, string[]]>).map(([label, ids]) => (
            <button key={label} type="button" className="btn btn-sm" onClick={() => onSelectSet(new Set(ids))}
              disabled={disabled || (label !== 'Clear' && ids.length === 0)}>
              {label}{label !== 'Clear' ? ` (${ids.length})` : ''}
            </button>
          ))}
          <button type="button" data-testid="fix-selected-top" className="btn btn-sm" disabled={disabled || selectedCount === 0} onClick={onDraft}
            style={{ marginLeft: 'auto', fontWeight: 700, background: selectedCount === 0 ? undefined : 'var(--accent, #3b82f6)', color: selectedCount === 0 ? undefined : '#fff' }}>
            Fix selected ({selectedCount})
          </button>
        </div>
      )}
      {tasks.length === 0 && <p>No open issues from your last sync. What would you like to change?</p>}
      {tasks.map((task) => (
        <label key={task.id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8, margin: '10px 0' }}>
          <input type="checkbox" disabled={disabled} checked={selected.has(task.id)} onChange={() => onToggleTask(task.id)} />
          <span><strong><span data-testid="task-priority" style={{ color: PRIORITY_COLOR[task.priority] ?? 'inherit', marginRight: 6 }}>{task.priority}</span>{task.title}</strong><br /><small>{task.file_path || 'No file specified'} — {oneLine(task.description)}</small></span>
        </label>
      ))}
      {guardianAvailable && <label style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8, margin: '10px 0' }}>
        <input type="checkbox" disabled={disabled} checked={guardian} onChange={onToggleGuardian} />
        <span>Turn on Guardian (watches for regressions on every push)</span>
      </label>}
      <button type="button" disabled={disabled || selectedCount === 0} onClick={onDraft}>Fix selected ({selectedCount})</button>
    </section>
  );
}

export function ProjectFixChecklist({
  projectId,
  selectionScope = projectId,
  onDraft,
  onSelectionChange,
  onSelectedIdsChange,
  onTasksChange,
  refreshKey = 0,
  disabled = false,
  loadTasks = loadOpenTasks,
  loadSyncStatus = api.syncStatus,
}: {
  projectId: string;
  /** Subscriber/session/project identity; a new scope starts a new selection. */
  selectionScope?: string;
  onDraft: (message: string) => void;
  /** Live composed request for the current selection (null when nothing is checked). */
  onSelectionChange?: (message: string | null) => void;
  /** Live ids of the checked tasks (empty until loaded / when nothing is checked). */
  onSelectedIdsChange?: (ids: string[]) => void;
  /** The loaded open tasks (empty until loaded), so a typed 'fix all' can mean all of them. */
  onTasksChange?: (tasks: ProjectSyncTask[]) => void;
  refreshKey?: number;
  disabled?: boolean;
  loadTasks?: (projectId: string) => Promise<ProjectSyncTask[]>;
  loadSyncStatus?: (projectId: string) => Promise<SyncStatus>;
}): React.ReactElement {
  const [tasks, setTasks] = useState<ProjectSyncTask[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [guardianAvailable, setGuardianAvailable] = useState(false);
  const [guardian, setGuardian] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'failed' | 'not_synced'>('loading');
  const [loadedProjectId, setLoadedProjectId] = useState<string | null>(null);
  const [loadedSelectionScope, setLoadedSelectionScope] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const selectionMemory = useRef<{ scope: string; initialized: boolean; choices: Map<string, boolean> }>({ scope: selectionScope, initialized: false, choices: new Map() });

  useEffect(() => {
    let cancelled = false;
    if (selectionMemory.current.scope !== selectionScope) {
      selectionMemory.current = { scope: selectionScope, initialized: false, choices: new Map() };
      setGuardian(false);
    }
    setRefreshing(true);
    if (!selectionMemory.current.initialized) {
      setLoadState('loading');
      setTasks([]);
      setSelected(new Set());
      setGuardianAvailable(false);
      setLastSyncedAt(null);
    }
    void Promise.all([
      loadTasks(projectId),
      loadSyncStatus(projectId),
    ]).then(([openTasks, status]) => {
      if (cancelled) return;
      setTasks(openTasks);
      const memory = selectionMemory.current;
      const next = new Set<string>();
      for (const task of openTasks) {
        const key = issueKey(task);
        const checked = memory.choices.get(key) ?? (!memory.initialized && (task.priority === 'P0' || task.priority === 'P1'));
        memory.choices.set(key, checked);
        if (checked) next.add(task.id);
      }
      memory.initialized = true;
      setSelected(next);
      setGuardianAvailable(Boolean(status.last_completed_at || status.last_run?.status === 'completed'));
      setLastSyncedAt(status.last_completed_at ?? null);
      setLoadedProjectId(projectId);
      setLoadedSelectionScope(selectionScope);
      const synced = Boolean(status.last_completed_at || status.last_run?.status === 'completed');
      setLoadState(openTasks.length === 0 && !synced ? 'not_synced' : 'ready');
    }).catch(() => {
      if (!cancelled) { setLoadedProjectId(projectId); setLoadedSelectionScope(selectionScope); setLoadState('failed'); }
    }).finally(() => { if (!cancelled) setRefreshing(false); });
    return () => { cancelled = true; };
  }, [loadSyncStatus, loadTasks, projectId, selectionScope, retry, refreshKey]);

  const toggleTask = (id: string) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    const task = tasks.find((item) => item.id === id);
    if (task) selectionMemory.current.choices.set(issueKey(task), next.has(id));
    return next;
  });

  useEffect(() => {
    onSelectionChange?.(loadedProjectId === projectId && loadedSelectionScope === selectionScope && loadState === 'ready' && (selected.size > 0 || guardian)
      ? composeFixSelectedMessage(tasks, selected, guardian) : null);
  }, [tasks, selected, guardian, onSelectionChange, loadedProjectId, projectId, loadedSelectionScope, selectionScope, loadState]);

  useEffect(() => {
    onSelectedIdsChange?.(loadedProjectId === projectId && loadedSelectionScope === selectionScope && loadState === 'ready' ? Array.from(selected) : []);
  }, [selected, onSelectedIdsChange, loadedProjectId, projectId, loadedSelectionScope, selectionScope, loadState]);

  useEffect(() => {
    onTasksChange?.(loadedProjectId === projectId && loadedSelectionScope === selectionScope && loadState === 'ready' ? tasks : []);
  }, [tasks, onTasksChange, loadedProjectId, projectId, loadedSelectionScope, selectionScope, loadState]);

  if (loadedProjectId !== projectId || loadedSelectionScope !== selectionScope || loadState === 'loading') return <p role="status">Loading findings from your last sync…</p>;
  if (loadState === 'failed') return <div role="alert">
    <p>We couldn't load your sync findings. Try again to see what needs fixing.</p>
    <button type="button" onClick={() => setRetry((value: number) => value + 1)}>Retry findings</button>
  </div>;
  if (loadState === 'not_synced') return <p>Run Sync on this project to find issues to fix.</p>;

  return <ProjectFixChecklistContent
    disabled={disabled || refreshing}
    tasks={tasks}
    selected={selected}
    guardianAvailable={guardianAvailable}
    guardian={guardian}
    lastSyncedAt={lastSyncedAt}
    onToggleTask={toggleTask}
    onSelectSet={(ids: Set<string>) => {
      for (const task of tasks) selectionMemory.current.choices.set(issueKey(task), ids.has(task.id));
      setSelected(ids);
    }}
    onToggleGuardian={() => setGuardian((value) => !value)}
    onDraft={() => onDraft(composeFixSelectedMessage(tasks, selected, guardian))}
  />;
}
